<script>
import { Dropdown } from 'bootstrap'
import { nextTick, ref } from 'vue'

export default {
  name: 'NewDropdown',
  emits: ['open', 'hidden'],
  props: {
    actions: { type: Array, default: () => [] },
    stopTogglerPropagation: { type: Boolean, default: false },
    alignRight: { type: Boolean, default: false },
    scrollable: { type: Boolean, default: false },
    autoClose: { type: [String, Boolean], default: true },
    strategy: { type: String, default: 'absolute' },
    disabled: { type: Boolean, default: false },
    hoverable: { type: Boolean, default: false },
  },
  setup() {
    const dropdownTriggerRef = ref(null)
    const isOpened = ref(false)

    return {
      dropdownTriggerRef,
      isOpened,
    }
  },
  computed: {
    hoverListeners() {
      return this.hoverable
        ? {
            onMouseenter: this.show,
            onMouseleave: this.closeFn,
          }
        : {}
    },
    actionsPresent() {
      return this.actions.length > 0
    },
  },
  watch: {
    dropdownTriggerRef: {
      handler(newEl, oldEl) {
        if (newEl && !oldEl) {
          new Dropdown(newEl, {
            offset: this.hoverable ? [0, -1] : [],
            autoClose: false,
            popperConfig: {
              strategy: this.strategy,
            },
          })
          newEl.addEventListener('show.bs.dropdown', () => {
            this.isOpened = true
            this.$emit('open', true)
          })
          newEl.addEventListener('hidden.bs.dropdown', () => {
            this.isOpened = false
            this.$emit('hidden', false)
          })
        }
      },
      immediate: true,
    },
    isOpened(value) {
      if (value) {
        this.$eventHub.$on('mouseup', this.closeIfAutoClose)
      } else {
        this.$eventHub.$off('mouseup', this.closeIfAutoClose)
      }
    },
    actions() {
      nextTick(() => {
        this.reposition()
      })
    },
  },
  unmounted() {
    this.$eventHub.$off('mouseup', this.closeIfAutoClose)
  },
  methods: {
    show() {
      if (this.dropdownTriggerRef) {
        Dropdown.getInstance(this.dropdownTriggerRef)?.show()
      }
    },
    reposition() {
      if (this.alignRight && this.dropdownTriggerRef && this.isOpened) {
        Dropdown.getInstance(this.dropdownTriggerRef)?.update()
      }
    },
    closeIfAutoClose(event) {
      if (
        this.autoClose &&
        !this.dropdownTriggerRef?.contains(event.target) &&
        this.dropdownTriggerRef != event.target
      ) {
        this.closeFn()
      }
    },
    closeFn() {
      if (this.dropdownTriggerRef) {
        Dropdown.getInstance(this.dropdownTriggerRef)?.hide()
      }
    },
    togglerClicked(event) {
      if (this.stopTogglerPropagation) event.stopPropagation()
    },
  },
}
</script>

<template lang="pug">
.dropdown(v-if="actionsPresent || !!$slots['items']" v-bind="hoverListeners" :class="{ disabled }")
  div(
    ref="dropdownTriggerRef"
    :data-bs-auto-close="autoClose"
    @click="togglerClicked($event)"
    data-bs-toggle="dropdown"
  )
    slot(:closeFn="closeFn" :show="show")
      button.btn.btn-sm.btn-default.dropdown-toggle
        i.bi-three-dots

  ul.dropdown-menu(:class="{ 'dropdown-menu-end': alignRight, scrollable }" @mouseup.prevent.stop)
    slot(:closeFn="closeFn" :isOpened="isOpened" name="items")
      template(v-for="action in actions")
        li(
          v-if="!!action.href && (!action.type || action.type == 'a')"
          :class="{ disabled: !!action.disabled }"
        )
          a.dropdown-item(
            v-if="!action.type || action.type == 'a'"
            :class="{ disabled: !!action.disabled }"
            :href="action.href"
            :target="action.target || '_self'"
          ) {{ action.title }}
        li(
          v-else
          :class="{ disabled: !!action.disabled }"
          @click.prevent.stop="action.clickFn && action.clickFn($event, closeFn)"
        )
          a.dropdown-item(
            v-if="!action.type || action.type == 'a'"
            :class="[{ disabled: !!action.disabled }, action.classes]"
          ) {{ action.title }}
          hr.dropdown-divider(v-if="action.type == 'divider'")
          h6.dropdown-header(v-if="action.type == 'header'" :class="action.classes")
            | {{ action.title }}
            .small.lh-1.text-black-30.ms-2.text-pre-wrap(v-if="action.subtitle?.length") {{ action.subtitle }}
</template>

<style lang="scss" scoped>
@import 'frontend_stylesheets/_variables.scss';
.dropdown {
  .dropdown-toggle {
    cursor: pointer;
  }
  .dropdown-menu {
    border: 0;
    box-shadow: $box-shadow-dropdown;
    border-radius: 8px;
    padding: 0.5rem;

    &.scrollable {
      max-height: 80vh;
      overflow: auto;
      @include for-size(small-and-medium-devices) {
        max-height: 60vh;
      }
    }
    .dropdown-item {
      cursor: pointer;
      color: var(--custom-primary);
      text-align: center;
      padding: 0.25rem;

      &:hover {
        background: $active-primary;
        border-radius: 5px;
      }
    }

    &:not(.dropdown-menu-end):not(.footer-top) {
      border-radius: 8px;
    }
  }
}
</style>
